import { ActionReducerMapBuilder, Draft } from '@reduxjs/toolkit';
import { finishLoad, initLoad } from '../../..';
import {
  DocumentRenderer,
  HTMLRenderer,
} from '../../../../../components/apia/execution/fields/apiaFinder/CellRenderers';
import {
  TResponsiveTableCellRenderer,
  TResponsiveTableColumn,
} from '../../../../../components/common/generic/responsiveTable/types';
import { TApiaFilter, TApiaFunctionPageInfo } from '../../../../../types';
import {
  IFinderSlice,
  TApiaFinderFilter,
  TApiaTableFilterConfig,
  TFinderFilter,
  TFinderState,
} from '../../../types';
import deleteFilters, {
  isFiltersAndRequired,
  isFiltersRowsAndNewPage,
} from './deleteFilters';
import loadStructure from './loadStructure';
import page from './page';
import sorter from './sort';
import updateData from './updateData';
import updateFilter from './updateFilters';
import { arrayOrArray } from '../../../../../utils/util';
import search from './search';
import loadSavedFilters from './loadSavedFilters';
import dayjs from 'dayjs';
import setFilters from './setFilters';
import queriesQueue from '../../../queueController';
import { getDateFormat } from '@apia/util';

function setPageInfo(
  state: Draft<TFinderState>,
  pageInfo?: TApiaFunctionPageInfo,
) {
  state.pageInfo = {
    ...(pageInfo as TApiaFunctionPageInfo),
    currentPage:
      pageInfo?.currentPage === '0' ? '1' : (pageInfo?.currentPage as string),
    pageCount:
      pageInfo?.pageCount === '0' ? '1' : (pageInfo?.pageCount as string),
  };
}
type TSavedDateFormat = {
  dateFrom: {
    fromStartOfValue: string;
    amountOf: number;
    fixedValue: string;
  };
  dateTo: { fixedValue: string };
  chkFromStartOf: boolean;
};
interface DateInputProps {
  chkFromStartOfValue: boolean;
  fromStartOfValue: string;
  amountOf: number;
  fixedValue: string;
}
export const calculateDate = (props: DateInputProps): string => {
  const { chkFromStartOfValue, fromStartOfValue, amountOf, fixedValue } = props;

  // Get the date format from the global variable
  const dateFormat = window.strDateFormat.toLocaleUpperCase();

  const now = dayjs();

  if (!chkFromStartOfValue) {
    // Return the fixed value if chkFromStartOfValue is false
    return fixedValue;
  }

  // Handle the case when chkFromStartOfValue is true
  switch (fromStartOfValue) {
    case 'actualDay':
      return now.startOf('day').format(dateFormat);
    case 'actualWeek':
      return now.startOf('week').format(dateFormat);
    case 'actualMonth':
      return now.startOf('month').format(dateFormat);
    case 'actualQuarter': {
      const currentMonth = now.month();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3; // Calculate the first month of the quarter
      return now.month(quarterStartMonth).startOf('month').format(dateFormat);
    }
    case 'actualSemester': {
      const currentMonth = now.month();
      const semesterStartMonth = currentMonth < 6 ? 0 : 6; // Calculate the first month of the semester
      return now.month(semesterStartMonth).startOf('month').format(dateFormat);
    }
    case 'actualYear':
      return now.startOf('year').format(dateFormat);
    case 'daysAmount':
      return now.subtract(amountOf, 'day').format(dateFormat);
    case 'weeksAmount':
      return now.subtract(amountOf, 'week').format(dateFormat);
    case 'monthsAmount':
      return now.subtract(amountOf, 'month').format(dateFormat);
    case 'quartersAmount': {
      const monthsToSubtract = amountOf * 3; // Each quarter equals 3 months
      return now.subtract(monthsToSubtract, 'month').format(dateFormat);
    }
    case 'semestersAmount': {
      const monthsToSubtract = amountOf * 6; // Each semester equals 6 months
      return now.subtract(monthsToSubtract, 'month').format(dateFormat);
    }
    case 'yearsAmount':
      return now.subtract(amountOf, 'year').format(dateFormat);
    default:
      throw new Error(`Invalid fromStartOfValue: ${fromStartOfValue}`);
  }
};

export default function addQueryActions(
  builder: ActionReducerMapBuilder<IFinderSlice>,
) {
  builder.addCase(loadStructure.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(loadStructure.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(loadStructure.fulfilled, (state, { payload, meta }) => {
    let promise: any = null;
    let finished: boolean = false;
    queriesQueue.runAction(
      'fromSearch',
      () => {
        return new Promise<void>((resolve) => {
          if (!finished) {
            promise = resolve;
          } else {
            resolve();
          }
        });
      },
      [
        'fromSearch',
        'fromSearchfromRefresh',
        'fromSearchfromUpdateFilters',
        'directUpdateFilters',
        'directRefresh',
        'directSearch',
      ],
    );

    if (payload) {
      const { fieldId } = meta.arg;
      const hasState = !!state.queries[fieldId];
      const columns = payload.columns
        .map((currentColumn) => {
          if (currentColumn.hide === '1') return false;
          const columnFilter = payload.filters.filter.find(
            (currentFilter) => currentFilter.column === currentColumn.name,
          );

          let renderer: TResponsiveTableCellRenderer | undefined;
          if (currentColumn.html) renderer = HTMLRenderer;
          if (currentColumn.document) renderer = DocumentRenderer;

          const returnColumn: TResponsiveTableColumn = {
            allowSorting: currentColumn.enableOrder === '1',
            showInAccordionTitle: currentColumn.asMainColumn,
            autoAdjustWidth: currentColumn?.autoWidth,
            currentSorting: currentColumn.order,
            label: currentColumn.title || currentColumn.name,
            name: currentColumn.name,
            showAsAdditional: currentColumn.asMoreInformation,
            title: !currentColumn.toolTip.trim()
              ? currentColumn.title
              : currentColumn.toolTip,
            width:
              Number.parseInt(currentColumn.width, 10) *
              (window.currentPalette?.queryColumnsMultiplier ?? 2),
            required: columnFilter?.required === '1',
            ...(renderer ? { renderer } : {}),
          };

          return returnColumn;
        })
        .filter(
          (currentColumn): currentColumn is TResponsiveTableColumn =>
            currentColumn !== false,
        );

      const actualFilters: TApiaFinderFilter[] = [];
      payload.filters.filter.forEach((currentFilter, id) => {
        if (
          currentFilter.hide ||
          !(typeof currentFilter === 'object' && currentFilter)
        )
          return;

        const isDateFilter =
          currentFilter.type === 'D' || currentFilter.type === 'date';
        const apiaFilter: TApiaFilter = {
          currentValue:
            (hasState
              ? currentFilter.currentValue
              : currentFilter.defaultValue) ??
            currentFilter.defaultCondition ??
            '',
          id: id + 1,
          column: currentFilter.column,
          options: currentFilter.filterOption
            ? [
                { label: '', value: '' },
                ...arrayOrArray(currentFilter.filterOption),
              ]
            : currentFilter.combo
              ? []
              : undefined,
          placeholder: currentFilter.title,
          title: currentFilter.title,
          toolTip: currentFilter.toolTip,
          type: currentFilter.type,
          asAdditional: currentFilter.asAdditional,
          readonly: currentFilter.readonly,
          sortCombo: currentFilter.orderCombo,
          required: isDateFilter
            ? currentFilter.dateRequired === '1' ||
              currentFilter.dateToRequired === '1' ||
              currentFilter.dateFromRequired === '1'
            : currentFilter.required === '1',
          dateFromRequired: currentFilter.dateFromRequired === '1',
          dateRequired: currentFilter.dateRequired === '1',
          dateToRequired: currentFilter.dateToRequired === '1',
          detectOnChange: currentFilter.detectOnChange,
          runAutomatically: payload.filters.runFiltersAutomatically,
          changeFiltersTimestamp: Date.now(),
          showAsInteger: currentFilter.showAsInteger,
          twoColumns: currentFilter.twoColumns,
        };
        const stateFilter = state.queries[meta.arg.fieldId].filters?.find(
          (f) => f.apiaFilter.id === id + 1,
        );
        if (
          stateFilter &&
          currentFilter.currentValue === stateFilter.apiaFilter.currentValue &&
          (currentFilter.required !== '0' ||
            currentFilter.dateRequired !== '0' ||
            currentFilter.dateToRequired !== '0' ||
            currentFilter.dateFromRequired !== '0') &&
          (stateFilter.apiaFilter.error ||
            stateFilter.apiaFilter.errorFrom ||
            stateFilter.apiaFilter.errorTo)
        ) {
          apiaFilter.error = stateFilter.apiaFilter.error;
          apiaFilter.errorFrom = stateFilter.apiaFilter.errorFrom;
          apiaFilter.errorTo = stateFilter.apiaFilter.errorTo;
        }
        if (isDateFilter) {
          apiaFilter.filterToId = `${id + 1}i`;
          apiaFilter.filterToValue = currentFilter.filterToValue;
        }
        const finderFilter: TFinderFilter = (({
          showHour,
          twoColumns,
          defaultCondition,
          fixedCondition,
          detectOnChange,
          defaultValue,
          readonly,
          orderCombo,
          asAdditional,
        }) => ({
          showHour,
          twoColumns,
          defaultCondition,
          fixedCondition,
          detectOnChange,
          defaultValue,
          readonly,
          orderCombo,
          asAdditional,
        }))(currentFilter);

        actualFilters.push({ apiaFilter, finderFilter });
        const index = (state.queries[fieldId].filters ?? []).findIndex(
          (current) => current.apiaFilter.id === `${apiaFilter.id}i`,
        );
        if (isDateFilter && index === -1) return;
        if (index !== -1)
          actualFilters.push((state.queries[fieldId].filters ?? [])[index]);
      });

      const filtersConfig: TApiaTableFilterConfig = {
        ...payload.filters,
        filter: undefined,
      };

      const { queryData } = payload;

      state.queries[fieldId].modalData = queryData;
      state.queries[fieldId].columns = columns;

      state.queries[fieldId].filters = actualFilters;
      state.queries[fieldId].filtersConfig = filtersConfig;
      state.queries[fieldId].loading -= 1;
      state.queries[fieldId].shouldShowModal = true;

      finishLoad(state, fieldId);
    }

    finished = true;
    (promise as () => void)?.();
  });
  builder.addCase(loadSavedFilters.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(loadSavedFilters.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(loadSavedFilters.fulfilled, (state, { payload, meta }) => {
    if (payload) {
      const { fieldId } = meta.arg;
      const hasState = !!state.queries[fieldId];
      const columns = payload.columns
        .map((currentColumn) => {
          if (currentColumn.hide === '1') return false;
          const columnFilter = payload.filters.filter.find(
            (currentFilter) => currentFilter.column === currentColumn.name,
          );

          let renderer: TResponsiveTableCellRenderer | undefined;
          if (currentColumn.html) renderer = HTMLRenderer;
          if (currentColumn.document) renderer = DocumentRenderer;

          const returnColumn: TResponsiveTableColumn = {
            allowSorting: currentColumn.enableOrder === '1',
            currentSorting: currentColumn.order,
            label: currentColumn.title || currentColumn.name,
            name: currentColumn.name,
            showAsAdditional: currentColumn.asMoreInformation,
            title: !currentColumn.toolTip.trim()
              ? currentColumn.title
              : currentColumn.toolTip,
            width:
              Number.parseInt(currentColumn.width, 10) *
              (window.currentPalette?.queryColumnsMultiplier ?? 2),
            required: columnFilter?.required === '1',
            ...(renderer ? { renderer } : {}),
          };

          return returnColumn;
        })
        .filter(
          (currentColumn): currentColumn is TResponsiveTableColumn =>
            currentColumn !== false,
        );

      const actualFilters: TApiaFinderFilter[] = [];
      payload.filters.filter.forEach((currentFilter, id) => {
        if (
          currentFilter.hide ||
          !(typeof currentFilter === 'object' && currentFilter)
        )
          return;

        const dateFormat = getDateFormat();
        const isDateFilter =
          currentFilter.type === 'D' || currentFilter.type === 'date';
        let dateFromValue = '';
        let dateToValue = '';
        let actualDateProps: TSavedDateFormat;
        if (isDateFilter && currentFilter.savedValue) {
          const dateProps = JSON.parse(
            currentFilter.savedValue,
          ) as TSavedDateFormat;
          dateFromValue = calculateDate({
            amountOf: dateProps.dateFrom.amountOf,
            chkFromStartOfValue: dateProps.chkFromStartOf,
            fromStartOfValue: dateProps.dateFrom.fromStartOfValue,
            fixedValue: dateProps.dateFrom.fixedValue
              ? dayjs(Number(dateProps.dateFrom.fixedValue)).format(dateFormat)
              : dateProps.dateFrom.fixedValue,
          });
          dateToValue = !dateProps.chkFromStartOf
            ? dateProps.dateTo.fixedValue
              ? dayjs(Number(dateProps.dateTo.fixedValue)).format(dateFormat)
              : dateProps.dateTo.fixedValue
            : dayjs().startOf('day').format(dateFormat);
          actualDateProps = dateProps;
          actualDateProps.dateFrom = {
            ...dateProps.dateFrom,
            fixedValue: dateFromValue,
          };
          actualDateProps.dateTo = {
            ...dateProps.dateTo,
            fixedValue: dateToValue,
          };
        }
        const apiaFilter: TApiaFilter = {
          currentValue: isDateFilter
            ? dateFromValue
            : ((hasState
                ? currentFilter.currentValue
                : currentFilter.defaultValue) ??
              currentFilter.defaultCondition ??
              ''),
          id: id + 1,
          savedValue: isDateFilter
            ? JSON.stringify(actualDateProps!)
            : currentFilter.savedValue,
          column: currentFilter.column,
          options: currentFilter.filterOption
            ? [
                { label: '', value: '' },
                ...arrayOrArray(currentFilter.filterOption),
              ]
            : currentFilter.combo
              ? []
              : undefined,
          placeholder: currentFilter.title,
          title: currentFilter.title,
          toolTip: currentFilter.toolTip,
          type: currentFilter.type,
          asAdditional: currentFilter.asAdditional,
          readonly: currentFilter.readonly,
          sortCombo: currentFilter.orderCombo,
          required: isDateFilter
            ? currentFilter.dateRequired === '1' ||
              currentFilter.dateToRequired === '1' ||
              currentFilter.dateFromRequired === '1'
            : currentFilter.required === '1',
          dateFromRequired: currentFilter.dateFromRequired === '1',
          dateRequired: currentFilter.dateRequired === '1',
          dateToRequired: currentFilter.dateToRequired === '1',
          detectOnChange: currentFilter.detectOnChange,
          runAutomatically: payload.filters.runFiltersAutomatically,
          changeFiltersTimestamp: Date.now(),
          showAsInteger: currentFilter.showAsInteger,
          twoColumns: currentFilter.twoColumns,
        };
        if (isDateFilter) {
          apiaFilter.filterToId = `${id + 1}i`;
          apiaFilter.filterToValue = dateToValue ?? currentFilter.filterToValue;
        }
        apiaFilter.options = apiaFilter.options?.map((o) => {
          if (o.value === apiaFilter.currentValue) {
            return { ...o, selected: true };
          }
          return o;
        });
        const finderFilter: TFinderFilter = (({
          showHour,
          twoColumns,
          defaultCondition,
          fixedCondition,
          detectOnChange,
          defaultValue,
          readonly,
          orderCombo,
          asAdditional,
        }) => ({
          showHour,
          twoColumns,
          defaultCondition,
          fixedCondition,
          detectOnChange,
          defaultValue,
          readonly,
          orderCombo,
          asAdditional,
        }))(currentFilter);

        actualFilters.push({ apiaFilter, finderFilter });
        const index = (state.queries[fieldId].filters ?? []).findIndex(
          (current) => current.apiaFilter.id === `${apiaFilter.id}i`,
        );
        if (isDateFilter && index === -1) return;
        if (index !== -1)
          actualFilters.push((state.queries[fieldId].filters ?? [])[index]);
      });

      const filtersConfig: TApiaTableFilterConfig = {
        ...payload.filters,
        filter: undefined,
      };

      const { queryData } = payload;

      state.queries[fieldId].modalData = queryData;
      state.queries[fieldId].columns = columns;
      state.queries[fieldId].filters = actualFilters;
      state.queries[fieldId].filtersConfig = filtersConfig;
      state.queries[fieldId].loading -= 1;
      state.queries[fieldId].shouldShowModal = true;

      finishLoad(state, fieldId);
    }
  });

  builder.addCase(page.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(page.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(page.fulfilled, (state, { payload, meta }) => {
    state.queries[meta.arg.listId].rows = payload.rows;

    state.queries[meta.arg.listId].lastSearchAppliedFilters =
      (state.queries[meta.arg.listId].filters?.filter(
        (current) =>
          current.apiaFilter.currentValue !== undefined &&
          current.apiaFilter.currentValue !== '',
      ).length ?? 0) +
      (state.queries[meta.arg.listId].filters?.filter(
        (current) =>
          current.apiaFilter.filterToValue !== undefined &&
          current.apiaFilter.filterToValue !== '',
      ).length ?? 0);

    setPageInfo(state.queries[meta.arg.listId], payload.pageInfo);

    state.queries[meta.arg.listId].hasSearched = true;
    finishLoad(state, meta.arg.listId);
  });

  builder.addCase(sorter.rejected, (_, payload) => {
    console.error(payload.error);
  });

  builder.addCase(updateData.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(updateData.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(updateData.fulfilled, (state, payload) => {
    state.queries[payload.meta.arg.fieldId].rows = payload.payload.rows;
    state.queries[payload.meta.arg.fieldId].hasSearched = true;

    setPageInfo(
      state.queries[payload.meta.arg.fieldId],
      payload.payload.pageInfo,
    );

    state.queries[payload.meta.arg.fieldId].lastSearchAppliedFilters =
      (state.queries[payload.meta.arg.fieldId].filters?.filter(
        (current) =>
          current.apiaFilter.currentValue !== undefined &&
          current.apiaFilter.currentValue !== '',
      ).length ?? 0) +
      (state.queries[payload.meta.arg.fieldId].filters?.filter(
        (current) =>
          current.apiaFilter.filterToValue !== undefined &&
          current.apiaFilter.filterToValue !== '',
      ).length ?? 0);
    state.queries[payload.meta.arg.fieldId].areAllFiltersApplied = true;
    finishLoad(state, payload.meta.arg.fieldId);
  });

  builder.addCase(updateFilter.fulfilled, (state, payload) => {
    finishLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(updateFilter.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(updateFilter.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });

  builder.addCase(deleteFilters.fulfilled, (state, { payload, meta }) => {
    if (payload) {
      state.queries[meta.arg.listId].filters = payload?.filters;
    }
    if (isFiltersAndRequired(payload)) {
      state.queries[meta.arg.listId].rows = [];
    }
    state.queries[meta.arg.listId].hasSearched = false;

    const currentPageInfo = state.queries[meta.arg.listId]?.pageInfo;
    if (isFiltersRowsAndNewPage(payload)) {
      setPageInfo(state.queries[meta.arg.listId], payload.newPageInfo);
    } else if (
      currentPageInfo &&
      payload?.filters?.some((current) => {
        return current.apiaFilter.required;
      })
    ) {
      state.queries[meta.arg.listId].pageInfo = {
        ...currentPageInfo,
        pageCount: '1',
        totalRecords: '0',
        reachedMax: false,
        prefix: '',
        currentPage: '1',
        selectOnlyOne: false,
      };
    }

    if (isFiltersRowsAndNewPage(payload)) {
      state.queries[meta.arg.listId].rows = payload.rows;
    }
    state.queries[meta.arg.listId].areAllFiltersApplied = true;
    finishLoad(state, meta.arg.listId, true, payload?.filters ?? undefined);
  });
  builder.addCase(deleteFilters.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(deleteFilters.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });

  builder.addCase(search.fulfilled, (state, { payload, meta }) => {
    if (payload && 'filters' in payload) {
      const { fieldId } = meta.arg;
      state.queries[fieldId].filters = payload?.filters;
    }
    state.queries[meta.arg.fieldId].areAllFiltersApplied = true;
    if (payload && 'filtersApplied' in payload) {
      state.queries[meta.arg.fieldId].areAllFiltersApplied =
        payload.filtersApplied;
    }
    finishLoad(state, meta.arg.fieldId);
  });
  builder.addCase(search.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(search.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });

  builder.addCase(setFilters.fulfilled, (state, { payload, meta }) => {
    if (payload && payload.filters) {
      const { fieldId } = meta.arg;
      state.queries[fieldId].filters = payload.filters;
      state.queries[fieldId].areAllFiltersApplied =
        payload.appliedFilters !== false;
    }
    finishLoad(state, meta.arg.fieldId);
  });
  builder.addCase(setFilters.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.fieldId);
  });
  builder.addCase(setFilters.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.fieldId);
  });
}
